import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast';
import { FiCheck } from 'react-icons/fi';
import { s3Upload } from '../../../Utils/s3Upload';
import axios from '../../../Utils/axiosInstance';

const BankTransferUploadSection = ({
  quotation,
  bankTransferFile,
  bankTransferDocument,
  setBankTransferFile,
  bankTransferUploading,
  setBankTransferUploading,
  bankTransferStatus,
  isBankTransferVerified,
  isBankTransferUploaded,
  isBankTransferRejected,
  refreshQuotations,
  grandTotal = 0,
  type = "Full_Order",
}) => {
  const [orgBankDetails, setOrgBankDetails] = useState(null);
  const [showOrgBankDetails, setShowOrgBankDetails] = useState(false);
  const [formStep, setFormStep] = useState("details"); // "details" | "upload"
  const [bankDetailsForm, setBankDetailsForm] = useState({
    bankName: "",
    accountHolderName: "",
    accountNumber: "",
    paymentMethod: "NEFT",
    transactionReference: "",
    transferDate: "",
    transferTime: "",
    buyerRemarks: "",
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    // Set organization bank details (static)
    setOrgBankDetails({
      bankName: process.env.REACT_APP_BANKACCOUNT_NAME,
      accountNumber: process.env.REACT_APP_BANKACCOUNT_NUMBER,
      accountHolderName: process.env.REACT_APP_BANKACCOUNT_NAME,
      ifscCode: process.env.REACT_APP_BANKACCOUNT_IFSC,
    });
  }, []);

  // ── Verified State ──────────────────────────────────────────────────────
  if (isBankTransferVerified) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-emerald-600 text-lg">✅</span>
          <p className="text-sm font-bold text-emerald-700">
            Bank Transfer Verified
          </p>
        </div>
        <p className="text-xs text-emerald-600 leading-relaxed">
          Your bank transfer has been verified by our team. Your order is now being processed.
        </p>
        <div className="mt-3 flex items-center gap-2 bg-emerald-100 rounded-xl px-3 py-2">
          <FiCheck className="text-emerald-700" size={14} />
          <span className="text-xs font-bold text-emerald-700">
            Order Confirmed
          </span>
        </div>
      </div>
    );
  }

  // ── Under Review State ──────────────────────────────────────────────────
  if (isBankTransferUploaded && !isBankTransferRejected) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-blue-600 text-lg">📋</span>
          <p className="text-sm font-bold text-blue-700">Bank Transfer Under Review</p>
        </div>
        <p className="text-xs text-blue-600 leading-relaxed">
          Your bank transfer proof has been submitted and is currently under review by our team.
          We will notify you once it is verified.
        </p>
        <div className="mt-3 flex items-center gap-2 bg-blue-100 rounded-xl px-3 py-2">
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-xs font-semibold text-blue-700">Awaiting admin verification</span>
        </div>
      </div>
    );
  }

  // ── Form Validation ────────────────────────────────────────────────────
  const validateBankDetailsForm = () => {
    const errors = {};

    if (!bankDetailsForm.bankName.trim()) errors.bankName = "Bank name is required";
    if (!bankDetailsForm.accountHolderName.trim()) errors.accountHolderName = "Account holder name is required";
    if (!bankDetailsForm.accountNumber.trim()) errors.accountNumber = "Account number is required";
    if (!bankDetailsForm.paymentMethod) errors.paymentMethod = "Payment method is required";
    if (!bankDetailsForm.transactionReference.trim()) errors.transactionReference = "Transaction reference is required";
    if (!bankDetailsForm.transferDate) errors.transferDate = "Transfer date is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (field, value) => {
    setBankDetailsForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleProceeedToUpload = () => {
    if (validateBankDetailsForm()) {
      setFormStep("upload");
    } else {
      toast.error("Please fill all required fields");
    }
  };

  // ── Main Form (Details + Upload) ────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* ── Organization Bank Details Card ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-blue-700 text-base">🏦</span>
          <p className="text-sm font-bold text-blue-700">Our Bank Details (Where to Transfer)</p>
        </div>
        <button
          onClick={() => setShowOrgBankDetails(!showOrgBankDetails)}
          className="text-xs text-blue-600 font-semibold hover:underline"
        >
          {showOrgBankDetails ? "Hide Details" : "Show Bank Details"}
        </button>

        {showOrgBankDetails && orgBankDetails && (
          <div className="mt-3 space-y-2 bg-white rounded-xl p-3 border border-blue-100">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 font-semibold">Bank Name:</span>
              <span className="text-gray-900 font-bold">{orgBankDetails.bankName}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 font-semibold">Account Number:</span>
              <span className="text-gray-900 font-bold">{orgBankDetails.accountNumber}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 font-semibold">Account Holder:</span>
              <span className="text-gray-900 font-bold">{orgBankDetails.accountHolderName}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 font-semibold">IFSC Code:</span>
              <span className="text-gray-900 font-bold">{orgBankDetails.ifscCode}</span>
            </div>
            <div className="flex justify-between text-xs mt-2 pt-2 border-t border-blue-100">
              <span className="text-gray-600 font-semibold">Transfer Amount:</span>
              <span className="text-primaryColor font-bold">₹{Number(grandTotal).toLocaleString("en-IN")}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Step 1: Bank Transfer Details Form ── */}
      {formStep === "details" && (
        <div className="space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
            <p className="text-xs text-green-700 leading-relaxed">
              Please fill in your bank transfer details below. Make sure the information matches your bank records exactly.
            </p>
          </div>

          {/* Bank Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Your Bank Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g., HDFC Bank, ICICI Bank"
              value={bankDetailsForm.bankName}
              onChange={(e) => handleFormChange("bankName", e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none transition ${formErrors.bankName
                ? "border-red-300 bg-red-50"
                : "border-gray-200 bg-white focus:border-primaryColor"
                }`}
            />
            {formErrors.bankName && (
              <p className="text-xs text-red-600 mt-1">{formErrors.bankName}</p>
            )}
          </div>

          {/* Account Holder Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Account Holder Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Full name of the account holder"
              value={bankDetailsForm.accountHolderName}
              onChange={(e) => handleFormChange("accountHolderName", e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none transition ${formErrors.accountHolderName
                ? "border-red-300 bg-red-50"
                : "border-gray-200 bg-white focus:border-primaryColor"
                }`}
            />
            {formErrors.accountHolderName && (
              <p className="text-xs text-red-600 mt-1">{formErrors.accountHolderName}</p>
            )}
          </div>

          {/* Account Number */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Your bank account number"
              value={bankDetailsForm.accountNumber}
              onChange={(e) => handleFormChange("accountNumber", e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none transition ${formErrors.accountNumber
                ? "border-red-300 bg-red-50"
                : "border-gray-200 bg-white focus:border-primaryColor"
                }`}
            />
            {formErrors.accountNumber && (
              <p className="text-xs text-red-600 mt-1">{formErrors.accountNumber}</p>
            )}
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Payment Method <span className="text-red-500">*</span>
            </label>
            <select
              value={bankDetailsForm.paymentMethod}
              onChange={(e) => handleFormChange("paymentMethod", e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none transition ${formErrors.paymentMethod
                ? "border-red-300 bg-red-50"
                : "border-gray-200 bg-white focus:border-primaryColor"
                }`}
            >
              <option value="NEFT">NEFT (Next day)</option>
              <option value="RTGS">RTGS (Same day)</option>
              <option value="IMPS">IMPS (Instant)</option>
              {/* <option value="UPI">UPI (Instant)</option>
              <option value="CHEQUE">Cheque</option> */}
            </select>
            {formErrors.paymentMethod && (
              <p className="text-xs text-red-600 mt-1">{formErrors.paymentMethod}</p>
            )}
          </div>

          {/* Transaction Reference */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Transaction Reference (UTR/Ref No) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g., 202401120000123456 or Cheque number"
              value={bankDetailsForm.transactionReference}
              onChange={(e) => handleFormChange("transactionReference", e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none transition ${formErrors.transactionReference
                ? "border-red-300 bg-red-50"
                : "border-gray-200 bg-white focus:border-primaryColor"
                }`}
            />
            {formErrors.transactionReference && (
              <p className="text-xs text-red-600 mt-1">{formErrors.transactionReference}</p>
            )}
          </div>

          {/* Transfer Date */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Transfer Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={bankDetailsForm.transferDate}
              onChange={(e) => handleFormChange("transferDate", e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none transition ${formErrors.transferDate
                ? "border-red-300 bg-red-50"
                : "border-gray-200 bg-white focus:border-primaryColor"
                }`}
            />
            {formErrors.transferDate && (
              <p className="text-xs text-red-600 mt-1">{formErrors.transferDate}</p>
            )}
          </div>

          {/* Transfer Time */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Transfer Time (Optional)
            </label>
            <input
              type="time"
              value={bankDetailsForm.transferTime}
              onChange={(e) => handleFormChange("transferTime", e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-primaryColor transition"
            />
          </div>

          {/* Buyer Remarks */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Additional Notes (Optional)
            </label>
            <textarea
              placeholder="Any additional information about the transfer (max 500 characters)"
              value={bankDetailsForm.buyerRemarks}
              onChange={(e) => handleFormChange("buyerRemarks", e.target.value.slice(0, 500))}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-primaryColor transition resize-none"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">
              {bankDetailsForm.buyerRemarks.length}/500 characters
            </p>
          </div>

          {/* Proceed Button */}
          <button
            onClick={handleProceeedToUpload}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-primaryColor hover:opacity-90 text-white font-bold text-sm transition shadow-lg"
          >
            ✓ Details Filled — Proceed to Upload Proof
          </button>
        </div>
      )}

      {/* ── Step 2: Upload File ── */}
      {formStep === "upload" && (
        <div className="space-y-3">
          {/* Filled Details Summary */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-1.5 text-xs">
            <p className="font-semibold text-gray-700 mb-2">Filled Details:</p>
            <div className="flex justify-between">
              <span className="text-gray-600">Bank:</span>
              <span className="font-semibold text-gray-900">{bankDetailsForm.bankName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Account Holder:</span>
              <span className="font-semibold text-gray-900">{bankDetailsForm.accountHolderName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Method:</span>
              <span className="font-semibold text-gray-900">{bankDetailsForm.paymentMethod}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Transaction Ref:</span>
              <span className="font-semibold text-gray-900">{bankDetailsForm.transactionReference}</span>
            </div>
            <button
              onClick={() => setFormStep("details")}
              className="text-blue-600 font-semibold hover:underline mt-2"
            >
              Edit Details
            </button>
          </div>

          {/* Upload Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
            <p className="text-xs text-blue-600 leading-relaxed">
              Now upload the screenshot or proof of your bank transfer (bank receipt, transaction confirmation, etc.)
            </p>
          </div>

          {/* File Input */}
          <label className="block">
            <span className="text-xs font-semibold text-gray-600 mb-1 block">
              Upload Transfer Proof (PDF, JPG, PNG, WEBP, DOC, DOCX - max 25 MB)
            </span>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.doc,.docx,.xls,.xlsx"
              disabled={bankTransferUploading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                if (file.size > 25 * 1024 * 1024) {
                  toast.error("File size must be less than 25 MB.");
                  return;
                }

                const allowedTypes = [
                  "application/pdf",
                  "image/jpeg",
                  "image/jpg",
                  "image/png",
                  "image/webp",
                  "image/heic",
                  "image/heif",
                  "image/gif",
                  "image/bmp",
                  "application/msword",
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  "application/vnd.ms-excel",
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                ];

                const fileExt = file.name.split(".").pop().toLowerCase();
                const allowedExts = [
                  "pdf", "jpg", "jpeg", "png", "webp", "heic", "heif", "gif", "bmp", "doc", "docx", "xls", "xlsx"
                ];

                if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
                  toast.error("Invalid file format. Please upload a valid document or image.");
                  return;
                }
                setBankTransferUploading(true);
                try {
                  const { url } = await s3Upload(file, "bank-transfers");

                  setBankTransferFile({
                    url,
                    name: file.name,
                    type: fileExt || "pdf",
                    sizeKb: Math.round(file.size / 1024),
                  });
                  toast.success("Proof ready. Click submit to proceed.");
                } catch (err) {
                  toast.error(err?.response?.data?.message || err?.message || "Upload failed. Please try again.");
                } finally {
                  setBankTransferUploading(false);
                }
              }}
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-blue-100/50 file:text-blue-700 hover:file:bg-blue-100 transition"
            />
          </label>

          {bankTransferFile && (
            <p className="text-xs text-green-600 font-semibold">
              ✓ {bankTransferFile.name} ready to submit
            </p>
          )}

          {isBankTransferRejected && bankTransferDocument?.adminRemarks && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs font-bold text-red-700 mb-1">Rejection Reason:</p>
              <p className="text-xs text-red-600">{bankTransferDocument.adminRemarks}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            disabled={!bankTransferFile || bankTransferUploading}
            onClick={async () => {
              if (!bankTransferFile) return;
              setBankTransferUploading(true);
              try {
                if (isBankTransferRejected && bankTransferDocument?._id) {
                  await axios.post(
                    `/bank-transfer/update/${bankTransferDocument._id}`,
                    {
                      file: {
                        ...bankTransferFile,
                        bankDetails: {
                          ...bankDetailsForm,
                          transferAmount: grandTotal,
                        },
                      },
                    }
                  );
                } else {
                  await axios.post("/bank-transfer/upload", {
                    orderId: quotation.orderId?._id || quotation.orderId,
                    selectedQuotationId: quotation._id,
                    type: type || "Full_Order",
                    file: {
                      ...bankTransferFile,
                      bankDetails: {
                        ...bankDetailsForm,
                        transferAmount: grandTotal,
                      },
                    },
                  });
                }
                toast.success(
                  "Bank transfer details submitted! Our team will verify shortly."
                );
                setBankTransferFile(null);
                setFormStep("details");
                setBankDetailsForm({
                  bankName: "",
                  accountHolderName: "",
                  accountNumber: "",
                  paymentMethod: "NEFT",
                  transactionReference: "",
                  transferDate: "",
                  transferTime: "",
                  buyerRemarks: "",
                });
                await refreshQuotations?.();
              } catch (err) {
                toast.error(
                  err?.response?.data?.message || "Submission failed."
                );
              } finally {
                setBankTransferUploading(false);
              }
            }}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-blue-600 hover:opacity-90 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg"
          >
            {bankTransferUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </>
            ) : isBankTransferRejected ? (
              "Re-upload Transfer Details & Proof"
            ) : (
              "Submit Transfer Details & Proof"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default BankTransferUploadSection